<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { resetPassword } from '@/services/api/auth'
import NewPasswordField from '@/components/authentication/fields/NewPasswordField.vue'
import AuthBrandPanel from '@/components/authentication/AuthBrandPanel.vue'
import AuthBrandLogo from '@/components/authentication/AuthBrandLogo.vue'
import { EnumPasswordStrength } from '@/enums/enums.js'
import { handleLoginError } from '@/utils/errors.js'
import LanguageSwitcherHorizontal from '@/components/common/LanguageSwitcherHorizontal.vue'
import { useAuthStore } from '@/stores/authStore.js'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const authStore = useAuthStore()

const newPasswordFieldRef = ref(null)
const login = route.query.login
const authorizationkey = route.query.authorizationkey
const newPassword = ref('')
const confirmPassword = ref('')
const loading = ref(false)
const errorMessage = ref('')

const resetBrandContent = computed(() => ({
    name: t('components.authentication.resetPassword.brand.name'),
    badge: t('components.authentication.resetPassword.brand.badge'),
    title: t('components.authentication.resetPassword.brand.title'),
    accent: t('components.authentication.resetPassword.brand.accent'),
    description: t('components.authentication.resetPassword.brand.description'),
    logo: {
        type: 'image',
        src: '/images/static/logos/logo-icon-white.webp',
        alt: t('components.authentication.resetPassword.brand.name'),
    },
    highlights: [
        {
            icon: 'enhanced_encryption',
            title: t('components.authentication.resetPassword.brand.advancedProtectionTitle'),
            description: t('components.authentication.resetPassword.brand.advancedProtectionDescription'),
        },
        {
            icon: 'lock_person',
            title: t('components.authentication.resetPassword.brand.exclusiveAccessTitle'),
            description: t('components.authentication.resetPassword.brand.exclusiveAccessDescription'),
        },
        {
            icon: 'support_agent',
            title: t('components.authentication.resetPassword.brand.supportTitle'),
            description: t('components.authentication.resetPassword.brand.supportDescription'),
        },
    ],
}))

const passwordInputType = computed(() => {
    const show = newPasswordFieldRef.value?.showPassword
    return show ? 'text' : 'password'
})

const passwordStrength = computed(() => {
    return newPasswordFieldRef.value?.passwordStrength || EnumPasswordStrength.Weak
})

const handleSubmit = async () => {
    errorMessage.value = ''

    if (newPassword.value !== confirmPassword.value) {
        errorMessage.value = t('components.authentication.resetPassword.passwordsDoNotMatch')
        return
    }

    if (passwordStrength.value !== EnumPasswordStrength.Strong) {
        errorMessage.value = t('common.messages.errors.invalidForce')
        return
    }

    loading.value = true

    try {
        await resetPassword({
            login,
            password: newPassword.value,
            authorizationkey,
        })
        message.success(t('common.messages.success.passwordCreate'))
        router.push(authStore.signInRoute)
    } catch (error) {
        errorMessage.value = await handleLoginError(error, t)
        console.error(error)
    } finally {
        loading.value = false
    }
}
</script>

<template>
    <div class="reset-password-area">
        <AuthBrandPanel :content="resetBrandContent" />

        <main class="reset-panel">
            <section class="reset-panel__content">
                <AuthBrandLogo
                    :content="resetBrandContent"
                    :logo-override="{ type: 'image', src: '/images/static/logos/logo-icon.png', alt: resetBrandContent.name }"
                    class="reset-panel__mobile-logo auth-brand-logo--mobile"
                />

                <div class="reset-panel__header">
                    <h2>{{ $t('components.authentication.resetPassword.panelTitle') }}</h2>
                    <p>{{ $t('components.authentication.resetPassword.panelSubtitle') }}</p>
                </div>

                <div class="reset-card">
                    <div v-if="errorMessage" class="reset-alert">
                        {{ errorMessage }}
                    </div>

                    <form class="reset-form" @submit.prevent="handleSubmit">
                        <div class="reset-form__group reset-form__group--password">
                            <label>{{ $t('components.authentication.resetPassword.newPasswordPlaceholder') }}</label>
                            <NewPasswordField
                                ref="newPasswordFieldRef"
                                v-model="newPassword"
                                :label="$t('components.authentication.resetPassword.newPasswordPlaceholder')"
                                :confirmPassword="confirmPassword"
                            />
                        </div>

                        <div class="reset-form__group">
                            <label for="confirm-password">{{ $t('components.authentication.resetPassword.confirmPassword') }}</label>
                            <div class="reset-form__field">
                                <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                    <path d="M7 11V7a5 5 0 0110 0v4" />
                                </svg>
                                <input
                                    id="confirm-password"
                                    v-model="confirmPassword"
                                    :type="passwordInputType"
                                    :placeholder="$t('components.authentication.resetPassword.confirmPasswordPlaceholder')"
                                    autocomplete="new-password"
                                    required
                                >
                            </div>
                        </div>

                        <button class="reset-form__submit" type="submit" :disabled="loading">
                            <span v-if="loading" class="reset-form__spinner" aria-hidden="true"></span>
                            <span>{{ loading ? $t('components.authentication.resetPassword.saving') : $t('components.authentication.resetPassword.setPassword') }}</span>
                            <svg v-if="!loading" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                                <path d="M5 12h14M12 5l7 7-7 7" />
                            </svg>
                        </button>
                    </form>
                </div>

                <p class="reset-panel__support">
                    {{ $t('components.authentication.resetPassword.backTo') }}
                    <RouterLink :to="authStore.signInRoute">
                        {{ $t('components.authentication.resetPassword.signIn') }}
                    </RouterLink>
                </p>

                <LanguageSwitcherHorizontal class="reset-panel__languages" />
            </section>
        </main>
    </div>
</template>

<style lang="scss" scoped>
.reset-password-area {
    min-height: 100vh;
    display: flex;
    font-family: Inter, sans-serif;
    background: #f8f9ff;
}

.reset-panel {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 32px;
    background: #f8f9ff;

    &__content {
        width: 100%;
        max-width: 420px;
    }

    &__header {
        margin-bottom: 32px;
        text-align: center;

        h2 {
            margin: 0 0 8px;
            color: #260944;
            font-size: 28px;
            font-weight: 800;
            line-height: 1.2;
        }

        p {
            margin: 0;
            color: #a9a9c8;
            font-size: 14px;
            line-height: 1.65;
        }
    }

    &__support {
        margin: 20px 0 0;
        color: #a9a9c8;
        font-size: 12px;
        text-align: center;

        a {
            color: #605dff;
            font-weight: 600;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    &__languages {
        margin-top: 18px;
    }

    &__mobile-logo {
        display: none !important;
    }
}

.reset-card {
    padding: 36px;
    border: 1px solid #eceef2;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 8px 40px rgba(96, 93, 255, 0.1);
}

.reset-alert {
    margin-bottom: 18px;
    padding: 12px 14px;
    border: 1px solid rgba(220, 53, 69, 0.16);
    border-radius: 10px;
    background: rgba(220, 53, 69, 0.08);
    color: #b42334;
    font-size: 13px;
    line-height: 1.45;
}

.reset-form {
    display: flex;
    flex-direction: column;
    gap: 20px;

    &__group {
        label {
            display: block;
            margin-bottom: 8px;
            color: #5b5b98;
            font-size: 13px;
            font-weight: 600;
        }

        &--password {
            :deep(.v-input) {
                margin-bottom: 0;
            }
        }
    }

    &__field {
        position: relative;
        color: #a9a9c8;

        > svg {
            position: absolute;
            top: 50%;
            left: 13px;
            transform: translateY(-50%);
            transition: color 0.18s;
            pointer-events: none;
        }

        input {
            width: 100%;
            min-height: 46px;
            padding: 13px 16px 13px 40px;
            border: 1.5px solid #eceef2;
            border-radius: 10px;
            outline: none;
            background: #f8f9ff;
            color: #260944;
            font-family: Inter, sans-serif;
            font-size: 14px;
            transition: border-color 0.18s, background 0.18s, box-shadow 0.18s;

            &::placeholder {
                color: #a9a9c8;
            }

            &:focus {
                border-color: #605dff;
                background: #fff;
                box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.08);
            }
        }

        &:focus-within {
            color: #605dff;
        }
    }

    &__submit {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: 100%;
        margin-top: 4px;
        padding: 15px;
        border: 0;
        border-radius: 10px;
        background: linear-gradient(135deg, #ffd056 0%, #ffe7389e 100%);
        box-shadow: 0 6px 24px rgba(96, 93, 255, 0.38);
        color: #2c2c2cd0;
        font-family: Inter, sans-serif;
        font-size: 15px;
        font-weight: 700;
        cursor: pointer;
        transition: box-shadow 0.2s, transform 0.2s, opacity 0.2s;

        &:hover:not(:disabled) {
            box-shadow: 0 10px 28px rgba(96, 93, 255, 0.42);
            transform: translateY(-1px);
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.72;
        }
    }

    &__spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(44, 44, 44, 0.28);
        border-top-color: #2c2c2c;
        border-radius: 50%;
        animation: reset-spin 0.8s linear infinite;
    }
}

@keyframes reset-spin {
    to {
        transform: rotate(360deg);
    }
}

@media only screen and (max-width: 960px) {
    .reset-password-area {
        flex-direction: column-reverse;
    }

    .reset-panel {
        padding: 42px 24px;

        &__mobile-logo {
            display: flex !important;
            width: fit-content;
            margin: 0 auto 28px;
            padding: 10px 14px;
            border-radius: 14px;
        }
    }
}

@media only screen and (max-width: 575px) {
    .reset-panel {
        padding: 34px 16px;

        &__header {
            margin-bottom: 24px;

            h2 {
                font-size: 24px;
            }
        }
    }

    .reset-card {
        padding: 24px 18px;
        border-radius: 14px;
    }
}
</style>
